// Template preview tab — full-width iframe with desktop/mobile toggle.

import { Monitor, Smartphone } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

type Device = 'desktop' | 'mobile'

interface PreviewTabProps {
  htmlContent: string
  sampleData: string
}

export function TemplatePreviewTab({ htmlContent, sampleData }: PreviewTabProps): React.JSX.Element {
  const [device, setDevice] = useState<Device>('desktop')
  const [previewHtml, setPreviewHtml] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const updatePreview = useCallback((content: string, data: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      let rendered = content
      try {
        const parsed = JSON.parse(data)
        for (const [key, value] of Object.entries(parsed)) {
          rendered = rendered.replaceAll(`{{${key}}}`, String(value))
        }
      } catch {
        // Invalid JSON — render without replacements
      }
      setPreviewHtml(rendered)
    }, 300)
  }, [])

  useEffect(() => {
    updatePreview(htmlContent, sampleData)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [htmlContent, sampleData, updatePreview])

  const iframeWidth = device === 'desktop' ? 600 : 375

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0">
      <div className="flex items-center justify-between border-b border-surface-3 px-4 py-2">
        <span className="text-xs font-medium uppercase tracking-wider text-text-secondary">
          Preview
        </span>
        <div className="flex rounded-lg bg-surface-2 p-0.5">
          <button
            type="button"
            onClick={() => setDevice('desktop')}
            className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
              device === 'desktop'
                ? 'bg-surface-0 text-text-primary shadow-sm'
                : 'text-text-tertiary hover:text-text-secondary'
            }`}
          >
            <Monitor className="h-3.5 w-3.5" />
            Desktop
          </button>
          <button
            type="button"
            onClick={() => setDevice('mobile')}
            className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
              device === 'mobile'
                ? 'bg-surface-0 text-text-primary shadow-sm'
                : 'text-text-tertiary hover:text-text-secondary'
            }`}
          >
            <Smartphone className="h-3.5 w-3.5" />
            Mobile
          </button>
        </div>
      </div>
      <div className="flex justify-center bg-[#f4f4f5] p-6" style={{ minHeight: 500 }}>
        <div
          className="overflow-hidden rounded-lg bg-white shadow-lg transition-all"
          style={{ width: iframeWidth, maxWidth: '100%' }}
        >
          <iframe
            srcDoc={previewHtml}
            title="Template preview"
            sandbox="allow-same-origin"
            className="w-full border-0"
            style={{ height: 600 }}
          />
        </div>
      </div>
    </div>
  )
}
